/**
 * Sentence trends — category slots (May 2026).
 *
 * Slot 1 is always average / median sale price (`AVERAGE_HOME_PRICE`).
 * Slots 2–3: walk `ORDER_AFTER_AVERAGE_SALE_PRICE` from top to bottom; take the
 * first two rows where `matches(metrics)` is true and the category `id` is not
 * already chosen (same metric won’t appear twice).
 *
 * If fewer than two rows match, remaining slots are filled from
 * `FALLBACK_METRIC_IDS` (first unused ids).
 */

export const CATEGORY_IDS = {
  AVERAGE_HOME_PRICE: "average_home_price",
  PRICE_PER_SQFT: "price_per_sqft",
  PCT_RECEIVED: "pct_received",
  UNITS_SOLD: "units_sold",
  AVG_DOM: "avg_dom",
  INVENTORY: "inventory",
};

/**
 * Priority after average sale price — one row per line, evaluated in order.
 * Labels are for logging / QA only; copy lives in `buildSentences.js`.
 */
export const ORDER_AFTER_AVERAGE_SALE_PRICE = [
  {
    id: CATEGORY_IDS.PRICE_PER_SQFT,
    label:
      "Price per sq ft — UP while avg sale price DOWN (special copy vs Rising Home Values)",
    matches: (m) => m.avgSalePricePerc < 0 && m.avgSqFtPricePerc >= 1,
  },
  {
    id: CATEGORY_IDS.PRICE_PER_SQFT,
    label: "Price per sq ft — UP (≥ +1% vs comparison period)",
    matches: (m) => m.avgSqFtPricePerc >= 1,
  },
  {
    id: CATEGORY_IDS.PCT_RECEIVED,
    label: "% Received of asking — HIGH (≥ 99%, avg sale price not down YoY)",
    matches: (m) => m.percReceived >= 99 && m.avgSalePricePerc >= 0,
  },
  {
    id: CATEGORY_IDS.AVG_DOM,
    label: "Days on market — ≤15 avg DOM (6-Months Trending)",
    matches: (m) => m.avgDom <= 15,
  },
  {
    id: CATEGORY_IDS.AVG_DOM,
    label: "Days on market — DECREASING (≤ −5% vs prior period; DOM > 15)",
    matches: (m) => m.avgDomPerc <= -5 && m.avgDom > 15,
  },
  {
    id: CATEGORY_IDS.INVENTORY,
    label: "Inventory — LOW",
    matches: (m) => m.inventoryTier === "Low",
  },
  {
    id: CATEGORY_IDS.UNITS_SOLD,
    label: "Units sold — UP (≥ +5% vs prior period)",
    matches: (m) => m.unitsSoldPerc >= 5,
  },
  {
    id: CATEGORY_IDS.INVENTORY,
    label: "Inventory — BALANCED",
    matches: (m) => m.inventoryTier === "Balanced",
  },
  {
    id: CATEGORY_IDS.AVG_DOM,
    label: "Days on market — FLAT (within ±5%; DOM > 15)",
    matches: (m) => Math.abs(m.avgDomPerc) <= 5 && m.avgDom > 15,
  },
  {
    id: CATEGORY_IDS.INVENTORY,
    label: "Inventory — HIGH",
    matches: (m) => m.inventoryTier === "High",
  },
  {
    id: CATEGORY_IDS.AVG_DOM,
    label: "Days on market — INCREASING (> +5%)",
    matches: (m) => m.avgDomPerc > 5,
  },
  {
    id: CATEGORY_IDS.PRICE_PER_SQFT,
    label: "Price per sq ft — DOWN (≤ −1%)",
    matches: (m) => m.avgSqFtPricePerc <= -1,
  },
  {
    id: CATEGORY_IDS.PCT_RECEIVED,
    label: "% Received of asking — LOW (< 99%)",
    matches: (m) => m.percReceived < 99,
  },
  {
    id: CATEGORY_IDS.UNITS_SOLD,
    label: "Units sold — DOWN (< 0%)",
    matches: (m) => m.unitsSoldPerc < 0,
  },
  {
    id: CATEGORY_IDS.PRICE_PER_SQFT,
    label: "Price per sq ft — STEADY (−1% < change < +1%)",
    matches: (m) => m.avgSqFtPricePerc > -1 && m.avgSqFtPricePerc < 1,
  },
];

const FALLBACK_METRIC_IDS = [
  CATEGORY_IDS.PRICE_PER_SQFT,
  CATEGORY_IDS.PCT_RECEIVED,
  CATEGORY_IDS.AVG_DOM,
  CATEGORY_IDS.INVENTORY,
  CATEGORY_IDS.UNITS_SOLD,
];

/**
 * @returns {string[]} Exactly 3 category ids; first is always average home price.
 */
export function selectThreeCategories(metrics) {
  const out = [CATEGORY_IDS.AVERAGE_HOME_PRICE];
  const used = new Set(out);

  for (const row of ORDER_AFTER_AVERAGE_SALE_PRICE) {
    if (out.length >= 3) break;
    if (!used.has(row.id) && row.matches(metrics)) {
      out.push(row.id);
      used.add(row.id);
    }
  }

  for (const id of FALLBACK_METRIC_IDS) {
    if (out.length >= 3) break;
    if (
      id === CATEGORY_IDS.PCT_RECEIVED &&
      metrics.percReceived >= 99 &&
      metrics.avgSalePricePerc < 0
    ) {
      continue;
    }
    if (!used.has(id)) {
      out.push(id);
      used.add(id);
    }
  }

  return out;
}

/** Human-readable lines for QA / devtools. */
export function describeCategorySelection(metrics) {
  const selected = selectThreeCategories(metrics);
  const lines = [
    `MLS ${metrics.mlsAreaID} — Slot 1 (fixed): ${CATEGORY_IDS.AVERAGE_HOME_PRICE}.`,
    "After average sale price, walk priority list (first match per unused id wins slots 2–3):",
  ];

  ORDER_AFTER_AVERAGE_SALE_PRICE.forEach((row, i) => {
    const hit = row.matches(metrics);
    lines.push(`  ${i + 1}. ${row.label} — matches: ${hit ? "yes" : "no"}`);
  });

  lines.push(
    "Fallback (unused ids, if slots still open):",
    `  ${FALLBACK_METRIC_IDS.join(", ")}`,
  );
  lines.push(`Resolved 3-tuple: ${selected.join(" → ")}`);
  return lines;
}
